import { Button, Group, LoadingOverlay, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";

import { useMutation } from "urql";
import { setLabelMutation, type TuneThreadSetLabelMutation, type TuneThreadSetLabelMutationVariables } from "./queries";

interface IProps {
    tuneThread: {
        id: number;
        label: string | null;
    };
}

function LabelForm({
    initialLabel,
    fetching,
    onSubmit,
}: {
    initialLabel: string;
    fetching: boolean;
    onSubmit: (label: string) => void;
}): JSX.Element {
    const form = useForm<{ label: string }>({
        initialValues: { label: initialLabel },
    });

    return (
        <form onSubmit={form.onSubmit(({ label }) => onSubmit(label))}>
            <Stack maw={600} mx="auto">
                <LoadingOverlay visible={fetching} />
                <TextInput label="Label" placeholder="Enter a label (optional)" {...form.getInputProps("label")} />
                <Group justify="flex-end" mt="md">
                    <Button type="submit" disabled={fetching}>
                        Save
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

function EditLabelButton({ tuneThread }: IProps): JSX.Element {
    const [updateResult, executeUpdate] = useMutation<TuneThreadSetLabelMutation, TuneThreadSetLabelMutationVariables>(
        setLabelMutation,
    );

    const handleSave = async (newLabel: string) => {
        const result = await executeUpdate({
            data: {
                tuneThreadId: tuneThread.id,
                label: newLabel.trim() === "" ? null : newLabel,
            },
        });
        if (result.error) {
            notifications.show({ title: "Failed to update label", message: result.error.message });
        } else {
            notifications.show({ title: "Thread updated", message: "Label updated" });
        }
    };

    const onClick = () =>
        modals.open({
            title: "Edit label",
            size: "600px",
            children: (
                <LabelForm
                    fetching={updateResult.fetching}
                    initialLabel={tuneThread.label ?? ""}
                    onSubmit={(value) => {
                        handleSave(value);
                        modals.closeAll();
                    }}
                />
            ),
        });

    return (
        <Button variant="light" onClick={onClick} disabled={updateResult.fetching}>
            Edit label
        </Button>
    );
}

export default EditLabelButton;
